/**
 * Sağ kenar sticky kontrol çubuğu — site genelinde görünür.
 *
 *  1) Ney (ambiyans) aç/kapa   — ses dosyası yoksa buton hiç görünmez
 *  2) Tanıtım filmini modalda aç
 *
 * Ambiyansın sahibi src/lib/ambientAudio.ts (tek <audio>); burası sadece kontrol.
 * Film oynarken ambiyans kısılır, film kapanınca geri yükselir.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import {
    bootAmbient,
    subscribeAmbient,
    toggleAmbient,
    rampAmbient,
    AMBIENT_VOLUME,
    AMBIENT_DUCKED,
} from '../lib/ambientAudio'
import './SiteAudioBar.css'

/* Video SESSİZ; anlatım dile göre ayrı bir <audio> ile senkron çalar.
   Alternatifi her dil için ayrı mp4 üretmekti — 7 × 11 MB. Bu yolla
   toplam ~19 MB kalıyor ve dil değişince video yeniden yüklenmiyor.

   Dosya adı BİLEREK değişti: /hiranova-tanitim-film.mp4 adresinde daha
   önce Türkçe sesli sürüm yayındaydı; tarayıcı/CDN önbelleği eski mp4'ü
   verince anlatımın üstüne Türkçe ses biniyordu. Aynı ada sessiz içerik
   koymak önbelleği kırmıyor — ad değişikliği kesin çözüm. */
const FILM_SRC = '/hiranova-film-sessiz.mp4'
const FILM_POSTER = '/hiranova-tanitim-film-poster.jpg'
const FILM_AUDIO = (lang: string) => `/ses/film-${lang}.mp3`
/* GEÇİCİ (owner 2026-08-08): kullanıcı şikayetleri üzerine yabancı dil
   seslendirmeleri askıya alındı; film her dilde Türkçe anlatımla oynar.
   mp3 dosyaları public/ses/ altında duruyor — geri açmak için aşağıdaki
   listeyi ['tr','en','de','ru','ar','fr','zh'] yap, başka değişiklik gerekmez. */
const FILM_LANGS = ['tr']

const SiteAudioBar = () => {
    const { t } = useTranslation()
    const [ambient, setAmbient] = useState({ ready: false, on: false })
    const [filmOpen, setFilmOpen] = useState(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const narrRef = useRef<HTMLAudioElement | null>(null)
    const closeRef = useRef<HTMLButtonElement | null>(null)
    const railRef = useRef<HTMLDivElement | null>(null)

    const lang = (i18n.language || 'tr').slice(0, 2)
    const filmLang = FILM_LANGS.includes(lang) ? lang : 'tr'

    /* Mobilde pill, header'daki Üyelik Başvurusu butonunun hemen altına aynı
       genişlikte hizalanır (owner). Buton width:auto (metne bağlı) olduğu için
       CSS'te sabit değer kırılgandı; geometri çalışma anında butondan okunur
       ve CSS değişkenleriyle .sab-rail'e aktarılır. Masaüstünde değişken
       atanmaz, dikey-merkez sağ kenar düzeni aynen kalır. */
    useEffect(() => {
        const align = () => {
            const rail = railRef.current
            if (!rail) return
            const mobile = window.matchMedia('(max-width: 860px)').matches
            const btn = document.querySelector('.reservation-btn')
            const header = document.querySelector('header')
            if (!mobile || !btn) {
                rail.style.removeProperty('--sab-top')
                rail.style.removeProperty('--sab-right')
                rail.style.removeProperty('--sab-w')
                return
            }
            const b = btn.getBoundingClientRect()
            const hBottom = header ? header.getBoundingClientRect().bottom : b.bottom
            rail.style.setProperty('--sab-top', `${Math.max(b.bottom, hBottom) + 8}px`)
            rail.style.setProperty('--sab-right', `${Math.round(window.innerWidth - b.right)}px`)
            rail.style.setProperty('--sab-w', `${Math.round(b.width)}px`)
        }
        // Header fontları/i18n yüklenince buton genişliyor — kısa gecikmeli tekrar
        align()
        const t1 = window.setTimeout(align, 300)
        const t2 = window.setTimeout(align, 1200)
        window.addEventListener('resize', align)
        return () => {
            window.clearTimeout(t1)
            window.clearTimeout(t2)
            window.removeEventListener('resize', align)
        }
    }, [lang])

    /* Senkron SADECE süreksiz olaylarda (play/seek/buffer sonrası) yapılır.
       İlk sürümde onTimeUpdate ile saniyede ~4 kez hizalıyorduk; mp3'te her
       currentTime ataması duyulabilir bir kesinti yarattığı için anlatım tüm
       dillerde "kesik kesik" geliyordu (kullanıcı şikayeti, 2026-08-08).
       İki medya elemanı aynı hızda oynar — 79 sn'de doğal kayma birkaç ms,
       sürekli düzeltme gerektirmez. */
    const syncNarration = useCallback(() => {
        const v = videoRef.current
        const a = narrRef.current
        if (!v || !a) return
        if (Math.abs(a.currentTime - v.currentTime) > 0.3) a.currentTime = v.currentTime
    }, [])

    useEffect(() => {
        bootAmbient()
        return subscribeAmbient(setAmbient)
    }, [])

    // Modal açık: ambiyans kısılır, sayfa kaydırması durur, ESC kapatır
    useEffect(() => {
        if (!filmOpen) return
        rampAmbient(AMBIENT_DUCKED)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFilmOpen(false) }
        window.addEventListener('keydown', onKey)
        closeRef.current?.focus()
        return () => {
            window.removeEventListener('keydown', onKey)
            document.body.style.overflow = prevOverflow
            rampAmbient(AMBIENT_VOLUME)
        }
    }, [filmOpen])

    const closeFilm = () => {
        videoRef.current?.pause()
        const a = narrRef.current
        if (a) { a.pause(); a.currentTime = 0 }
        setFilmOpen(false)
    }

    return (
        <>
            <div ref={railRef} className="sab-rail" role="group" aria-label={t('siteAudio.railLabel')}>
                {ambient.ready && (
                    <button
                        type="button"
                        className={`sab-btn${ambient.on ? ' is-on' : ''}`}
                        onClick={toggleAmbient}
                        aria-pressed={ambient.on}
                        aria-label={ambient.on ? t('siteAudio.muteNey') : t('siteAudio.unmuteNey')}
                        title={ambient.on ? t('siteAudio.muteNey') : t('siteAudio.unmuteNey')}
                    >
                        {ambient.on ? (
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 9.5v5a1 1 0 0 0 1 1h3l4.2 3.4a.8.8 0 0 0 1.3-.62V5.72a.8.8 0 0 0-1.3-.62L8 8.5H5a1 1 0 0 0-1 1z" />
                                <path d="M17 8.8a4.5 4.5 0 0 1 0 6.4" />
                                <path d="M19.6 6.2a8 8 0 0 1 0 11.6" />
                            </svg>
                        ) : (
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 9.5v5a1 1 0 0 0 1 1h3l4.2 3.4a.8.8 0 0 0 1.3-.62V5.72a.8.8 0 0 0-1.3-.62L8 8.5H5a1 1 0 0 0-1 1z" />
                                <path d="M17 9.5l5 5" />
                                <path d="M22 9.5l-5 5" />
                            </svg>
                        )}
                    </button>
                )}

                {/* Etiketli pill — ikon tek başına ne olduğunu anlatmıyordu (owner) */}
                <button
                    type="button"
                    className="sab-film"
                    onClick={() => setFilmOpen(true)}
                    aria-label={t('siteAudio.openFilm')}
                    title={t('siteAudio.openFilm')}
                >
                    <span className="sab-film-icon" aria-hidden="true">
                        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M9 7.2l8.4 4.8L9 16.8z" fill="currentColor" />
                        </svg>
                    </span>
                    <span className="sab-film-text">{t('siteAudio.filmLabel')}</span>
                </button>
            </div>

            {filmOpen && (
                <div
                    className="sab-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('siteAudio.openFilm')}
                    onClick={closeFilm}
                >
                    <div className="sab-modal-inner" onClick={(e) => e.stopPropagation()}>
                        <button
                            ref={closeRef}
                            type="button"
                            className="sab-modal-close"
                            onClick={closeFilm}
                            aria-label={t('siteAudio.closeFilm')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>
                        {/* Video sessiz; anlatım aşağıdaki <audio>'dan gelir.
                            Ses kontrolü videonun kendi kontrollerinden yapılır. */}
                        <video
                            ref={videoRef}
                            className="sab-modal-video"
                            src={FILM_SRC}
                            poster={FILM_POSTER}
                            controls
                            autoPlay
                            playsInline
                            preload="metadata"
                            /* Savunma: dosya sessiz ama eski önbellekten sesli
                               sürüm gelirse bile video kanalı asla duyulmasın */
                            muted
                            onPlay={() => {
                                const a = narrRef.current
                                if (!a) return
                                a.currentTime = videoRef.current?.currentTime ?? 0
                                a.play().catch(() => { })
                            }}
                            onPause={() => narrRef.current?.pause()}
                            onSeeked={syncNarration}
                            /* Video buffer'a takılırsa anlatım tek başına akmasın:
                               beklerken sustur, akış dönünce hizala ve devam et */
                            onWaiting={() => narrRef.current?.pause()}
                            onPlaying={() => {
                                const v = videoRef.current
                                const a = narrRef.current
                                if (!v || !a || v.paused) return
                                syncNarration()
                                a.play().catch(() => { })
                            }}
                            onRateChange={() => {
                                const v = videoRef.current
                                if (v && narrRef.current) narrRef.current.playbackRate = v.playbackRate
                            }}
                            onEnded={() => narrRef.current?.pause()}
                        />
                        {/* key: dil değişince kaynak gerçekten yeniden yüklensin */}
                        <audio
                            key={filmLang}
                            ref={narrRef}
                            src={FILM_AUDIO(filmLang)}
                            preload="auto"
                        />
                    </div>
                </div>
            )}
        </>
    )
}

export default SiteAudioBar
